import { useState } from "react";
import { Plus, Trash2, Save, X } from "lucide-react";
import { adminApi } from "../../api";

const TIPOS = [
  { value: "VOLUME_CURSO", label: "Volume por curso (inscritos)" },
  { value: "QUANTIDADE_EMPRESA", label: "Quantidade intervenções (empresa)" },
];

const blankDesc = (ord) => ({ tipo: "VOLUME_CURSO", min_qtd: 0, max_qtd: null, desconto_pct: 0, descricao: "", ordem: ord });

function DescRowEdit({ idPrecario, item, onSaved, onCancel, onError }) {
  const [f, setF] = useState({
    tipo: item.Tipo || item.tipo || "VOLUME_CURSO",
    min_qtd: item.MinQtd ?? item.min_qtd ?? 0,
    max_qtd: item.MaxQtd ?? item.max_qtd ?? "",
    desconto_pct: item.DescontoPct ?? item.desconto_pct ?? 0,
    descricao: item.Descricao || item.descricao || "",
    ordem: item.Ordem ?? item.ordem ?? 0,
  });
  const [busy, setBusy] = useState(false);
  const idDesc = item.ID_PrecarioDesconto || null;
  const update = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    setBusy(true);
    const payload = {
      tipo: f.tipo,
      min_qtd: Number(f.min_qtd || 0),
      max_qtd: f.max_qtd === "" ? null : Number(f.max_qtd),
      desconto_pct: Number(f.desconto_pct || 0),
      descricao: f.descricao || null,
      ordem: Number(f.ordem || 0),
    };
    try {
      if (idDesc) await adminApi.precarioDescontoUpdate(idPrecario, idDesc, payload);
      else await adminApi.precarioDescontoCreate(idPrecario, payload);
      onSaved?.();
    } catch (e) { onError?.(e.message); }
    finally { setBusy(false); }
  };

  return (
    <tr className="bg-amber-50/40 border-b-2 border-amber-200" data-testid={`desconto-edit-${idDesc || "new"}`}>
      <td colSpan={6} className="p-2.5">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
          <label className="flex flex-col gap-0.5 md:col-span-2">
            <span className="text-[10px] uppercase text-[#978D78] font-bold">Tipo</span>
            <select value={f.tipo} onChange={(e) => update("tipo", e.target.value)} className="border border-[#E6E1D6] rounded px-1.5 py-1" data-testid="desconto-edit-tipo">
              {TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase text-[#978D78] font-bold">Min Qtd</span>
            <input type="number" value={f.min_qtd ?? 0} onChange={(e) => update("min_qtd", e.target.value)} className="border border-[#E6E1D6] rounded px-1.5 py-1" />
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase text-[#978D78] font-bold">Max Qtd (vazio = ilimitado)</span>
            <input type="number" value={f.max_qtd ?? ""} onChange={(e) => update("max_qtd", e.target.value)} className="border border-[#E6E1D6] rounded px-1.5 py-1" />
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase text-[#978D78] font-bold">Desconto %</span>
            <input type="number" step="0.01" value={f.desconto_pct ?? 0} onChange={(e) => update("desconto_pct", e.target.value)} className="border border-[#E6E1D6] rounded px-1.5 py-1 tabular-nums" data-testid="desconto-edit-pct" />
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase text-[#978D78] font-bold">Ordem</span>
            <input type="number" value={f.ordem ?? 0} onChange={(e) => update("ordem", e.target.value)} className="border border-[#E6E1D6] rounded px-1.5 py-1" />
          </label>
          <label className="flex flex-col gap-0.5 md:col-span-6">
            <span className="text-[10px] uppercase text-[#978D78] font-bold">Descrição</span>
            <input value={f.descricao || ""} onChange={(e) => update("descricao", e.target.value)} className="border border-[#E6E1D6] rounded px-1.5 py-1" />
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 mt-2">
          <button onClick={onCancel} className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-[#E6E1D6] rounded hover:bg-[#FAF8F2]" data-testid="desconto-edit-cancel"><X size={12} /> Cancelar</button>
          <button onClick={submit} disabled={busy} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-bold uppercase tracking-wider bg-[#0F1829] text-white rounded hover:bg-black disabled:opacity-40" data-testid="desconto-edit-save"><Save size={12} /> {busy ? "..." : "Guardar"}</button>
        </div>
      </td>
    </tr>
  );
}

export default function PrecarioDescontosTable({ idPrecario, descontos, onChanged, onError }) {
  const [editingId, setEditingId] = useState(null);
  const [adding, setAdding] = useState(false);

  const onDel = async (id) => {
    if (!window.confirm("Remover esta regra de desconto?")) return;
    try {
      await adminApi.precarioDescontoDelete(idPrecario, id);
      onChanged?.();
    } catch (e) { onError?.(e.message); }
  };

  return (
    <div className="bg-white border border-[#E6E1D6] rounded-md overflow-hidden" data-testid="precario-descontos-table">
      <div className="px-4 py-2.5 border-b border-[#F3F0E7] bg-[#FAF8F2] flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-[0.18em] text-[#978D78] font-bold">Regras de desconto ({descontos.length})</span>
        <button onClick={() => { setAdding(true); setEditingId(null); }} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold uppercase tracking-wider bg-[#0F1829] text-white rounded hover:bg-black" data-testid="desconto-add-btn">
          <Plus size={12} /> Nova regra
        </button>
      </div>
      <table className="w-full text-sm">
        <thead className="text-left text-[11px] uppercase tracking-[0.12em] text-[#978D78]">
          <tr className="border-b border-[#F3F0E7]">
            <th className="px-3 py-2">Tipo</th>
            <th className="px-3 py-2">Min</th>
            <th className="px-3 py-2">Max</th>
            <th className="px-3 py-2">Desconto</th>
            <th className="px-3 py-2">Descrição</th>
            <th className="px-3 py-2 text-right">Acções</th>
          </tr>
        </thead>
        <tbody>
          {adding && (
            <DescRowEdit
              idPrecario={idPrecario}
              item={blankDesc(descontos.length + 1)}
              onSaved={() => { setAdding(false); onChanged?.(); }}
              onCancel={() => setAdding(false)}
              onError={onError}
            />
          )}
          {descontos.map((d) => (
            editingId === d.ID_PrecarioDesconto
              ? <DescRowEdit key={d.ID_PrecarioDesconto} idPrecario={idPrecario} item={d}
                  onSaved={() => { setEditingId(null); onChanged?.(); }}
                  onCancel={() => setEditingId(null)}
                  onError={onError} />
              : <tr key={d.ID_PrecarioDesconto} className="border-b border-[#F3F0E7] hover:bg-[#FAF8F2]" data-testid={`desconto-row-${d.ID_PrecarioDesconto}`}>
                  <td className="px-3 py-2 text-xs font-mono text-[#978D78]">{d.Tipo}</td>
                  <td className="px-3 py-2 tabular-nums">{d.MinQtd}</td>
                  <td className="px-3 py-2 tabular-nums">{d.MaxQtd ?? "∞"}</td>
                  <td className="px-3 py-2 tabular-nums font-bold text-green-700">{Number(d.DescontoPct).toFixed(2)}%</td>
                  <td className="px-3 py-2 text-xs">{d.Descricao || "—"}</td>
                  <td className="px-3 py-2 text-right space-x-1">
                    <button onClick={() => { setEditingId(d.ID_PrecarioDesconto); setAdding(false); }} className="text-xs px-2 py-1 border border-[#E6E1D6] rounded hover:bg-[#FAF8F2]" data-testid={`desconto-edit-btn-${d.ID_PrecarioDesconto}`}>Editar</button>
                    <button onClick={() => onDel(d.ID_PrecarioDesconto)} className="text-xs px-2 py-1 border border-red-200 text-red-700 rounded hover:bg-red-50" data-testid={`desconto-del-btn-${d.ID_PrecarioDesconto}`}><Trash2 size={11} className="inline" /></button>
                  </td>
                </tr>
          ))}
          {!descontos.length && !adding && (
            <tr><td colSpan={6} className="px-3 py-6 text-center text-sm text-[#6B7280]">Sem regras de desconto.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
